import React, { useMemo } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { IRT, OWNER } from "constants/index"
import { OPEN_ITEM_ACTIONS_BOTTOM_SHEET } from "constants/events"
import { formatThousands } from "utils/numberUtils"
import { useAppSelector } from "appRedux/hooks"
import { updateShowCommentsBottomSheet } from "appRedux/slices/wishListSlice"
import MoreIcon from "components/atoms/_icons/MoreIcon"
import IconWrapper from "components/atoms/IconWrapper"
import ExpandableText from "components/atoms/ExpandableText"
import WishItemReactionBox from "components/molecules/WishItemReactionBox"
import TrueWishSection from "components/organisms/WishItemBottomSection/TrueWishSection"

interface WishItemBottomSectionProps extends SingleWishItem {
  containerId: string;
}

export default function WishItemBottomSection(props: WishItemBottomSectionProps) {
  const { containerId, ...item } = props

  const role = useAppSelector((state) => state.global.role)

  const dispatch = useDispatch()
  const { t } = useTranslation()

  function handleOpenMoreActionsBottomSheet() {
    dispatch(updateShowCommentsBottomSheet(false))
    document.dispatchEvent(new CustomEvent(OPEN_ITEM_ACTIONS_BOTTOM_SHEET))
  }

  const itemPrice = useMemo(() => {
    const price = item.price
    if (!price) return price
    const currency = item.currency
    if (currency !== IRT) return String(price)
    return formatThousands(price)
  }, [item.price, item.currency])

  return (
    <>
      <Container className="wishitem-bottom-section swiper-no-swiping">
        <ItemInfoContainer>
          <Stack direction="row" gap="8px" justifyContent="space-between" alignItems="flex-start">
            <Name>
              <ExpandableText text={item.name}/>
            </Name>
            <IconWrapper onClick={handleOpenMoreActionsBottomSheet}>
              <MoreIcon/>
            </IconWrapper>
          </Stack>
          {item.caption &&
              <Caption>
                  <ExpandableText text={item.caption}/>
              </Caption>}
          {item.url && <Link href={item.url} target="_blank">{item.url && t("general.viewLink")}</Link>}
        </ItemInfoContainer>
        <Stack direction="row-reverse" justifyContent="space-between">
          <WishItemReactionBox
            id={item.id}
            isLiked={item.isLiked}
            likeCount={item.likeCount}
            commentCount={item.commentCount}
            color={"#FFFFFF"}
          />
          {(item.price && item.currency) && <PriceWrapper>
              <span className={`price ${item.currency === IRT ? "farsi-digits" : ""}`}>{itemPrice}</span>
              <span className="currency">{t(`currency.${item.currency}`)}</span>
          </PriceWrapper>}
        </Stack>
        {(role === OWNER || item.cameTrue) && <TrueWishSection
            id={item.id}
            cameTrue={item.cameTrue}
        />}
      </Container>
      <Gradient/>
    </>
  )
}

const Container = styled(Stack)(() => ({
  width: "100%",
  position: "absolute",
  left: 0,
  bottom: 0,
  padding: "40px 12px 30px",
  zIndex: 1300,
  direction: "ltr",
  textAlign: "left"
}))

const ItemInfoContainer = styled(Stack)(({ theme }) => ({
  marginBottom: "16px",

  "& svg": {
    stroke: `${theme.palette.gray["1"]} !important`
  }
}))

const Name = styled("div")(({ theme }) => ({
  width: "100%",
  marginTop: "12px",

  "& p": {
    fontWeight: `500 !important`,
    fontSize: "1.4rem !important",
    color: `${theme?.palette.gray["1"]} !important`,
    width: "100% !important"
  }
}))

const Caption = styled("div")(({ theme }) => ({
  fontWeight: 400,
  fontSize: "1.2rem",
  color: theme?.palette.gray["1"],
  marginBottom: "8px",
  overflow: "hidden",
  textOverflow: "ellipsis"
}))

const Link = styled("a")(({ theme }) => ({
  color: theme.palette.info["1"],
  fontSize: "1.1rem",
  textDecoration: "none"
}))

const Gradient = styled("div")(() => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "200px",
  background: "linear-gradient(0, #000000, transparent)",
  position: "absolute",
  left: 0,
  bottom: 0,
  zIndex: 2
}))

const PriceWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  padding: "8px 12px",
  borderRadius: "32px",
  backgroundColor: "rgba(17,17,17,0.55)",
  color: theme.palette.gray[1],

  "& span.price": {
    fontSize: "14px",
    fontWeight: 500
  },

  "& span.currency": {
    fontSize: "12px"
  }
}))

